// owner-map: OP-09.spa
import { postBugReport } from "../services/bugReportApi.js";
import { useState, useRef, useCallback, useEffect } from "react";
import { beginIntent } from "../lib/telemetry/instrument";
import { ringBuffer, traceRing, recordUiEvent, captureLayoutSnapshot } from "../lib/logRingBuffer";
import { resolveBugReportTrace } from "../lib/bugReportTrace";
import { CLIENT_BUILD_SHA } from "../lib/telemetry/buildSha.js";
import { bugReportSessionFields } from "../lib/telemetry/bugReport.js";
import { telemetryTabId } from "../services/telemetry.js";
import { p2Telemetry } from "../services/p2Telemetry.js";
import { toast } from "../lib/ui/toast.js";
import { captureChatTarget } from "../lib/forecast/chatTarget.js";
import { getRunState } from "../store/runStore.js";
import {
  MAX_BUG_SCREENSHOTS,
  createAttachSessions,
  drawToCanvas,
  encodeCanvas,
  imageFileToDataUrl,
  imageFilesFrom,
} from "../lib/bugReportImages.js";
import { reportFailure } from "../lib/failureCounts.js";

const FEEDBACK_FAILED = "Couldn't send your feedback. Try again in a minute.";
const ATTACH_FAILED = "Couldn't attach that image.";

/**
 * Bug-report domain: the form state, the synchronous capture lock ref, and its handlers
 * (openBugReport, captureScreenshot, attachImageFiles, removeScreenshot, submitBug). While
 * the modal is open, an image pasted anywhere in the page is attached to the report.
 *
 * submitBug reads active/activeTrace/lastObs from AppInner (forecast context)
 * and calls setLastBugObs to stamp the response obs into the shared obs state.
 *
 * @param {{ active: object|null, activeTrace: object|null, lastObs: object|null, setLastBugObs: Function, pushSayerMsgRef: {current: Function|null} }} args
 *   pushSayerMsgRef is a commit-synced bridge to pushSayerMsg (defined after this
 *   hook in AppInner; the ref is written by a useLayoutEffect after each commit, the
 *   resumeTourRef pattern); submitBug appends the submit confirmation/failure line,
 *   through it, to the chat of the forecast on screen when Submit was clicked.
 * @returns {{ bugOpen, setBugOpen, bugDesc, setBugDesc, bugSteps, setBugSteps,
 *             bugSubmitting, bugMode, setBugMode, bugScreenshots, bugAttaching, bugCapturing, bugHidden,
 *             openBugReport, captureScreenshot, attachImageFiles, removeScreenshot,
 *             submitBug }}
 */
export function useBugReport({ active, activeTrace, lastObs, setLastBugObs, pushSayerMsgRef }) {
  const [bugOpen, setBugOpen] = useState(false);
  const [bugDesc, setBugDesc] = useState("");
  const [bugSteps, setBugSteps] = useState("");
  const [bugSubmitting, setBugSubmitting] = useState(false);
  const [bugMode, setBugMode] = useState("bug"); // "bug" | "feature"
  const [bugScreenshots, setBugScreenshotsState] = useState([]); // data-URLs of attached images, in order (max MAX_BUG_SCREENSHOTS)
  const bugScreenshotsRef = useRef([]); // the same list, read live by async attaches; written only by setBugScreenshots
  const setBugScreenshots = useCallback((next) => {
    bugScreenshotsRef.current = next;
    setBugScreenshotsState(next);
  }, []);
  // `{ bugId, token }` from a 503 `{ok:false, bug_id, retry_token}` (nothing stored the report):
  // the next submit of this draft carries the pair so the server reuses the id its bundles
  // already sit under. Cleared by a stored report and by a fresh form.
  const bugRetryRef = useRef(null);
  // Bumped by every fresh open. A submit snapshots it, and a late answer (the request was still
  // in flight when the reporter opened a fresh form) must not re-arm the pair the open cleared.
  const bugFormGenRef = useRef(0);
  // Attaches still decoding, per form session (lib/bugReportImages.js); bugAttaching mirrors
  // its count so Send waits for them.
  const [attachSessions] = useState(createAttachSessions);
  const [bugAttaching, setBugAttaching] = useState(0);
  // The current session's attach batches, chained so each is added in the order it began
  // (attachImageFiles); a new session starts a fresh chain, never waiting on a stale decode.
  const attachQueueRef = useRef(Promise.resolve());
  const beginAttach = useCallback(() => {
    const token = attachSessions.begin();
    setBugAttaching(attachSessions.pending);
    return token;
  }, [attachSessions]);
  // False when the form was reopened or sent while the attach ran: drop its images.
  const endAttach = useCallback(
    (token) => {
      const current = attachSessions.end(token);
      if (current) setBugAttaching(attachSessions.pending);
      return current;
    },
    [attachSessions],
  );
  const newAttachSession = useCallback(() => {
    attachSessions.reset();
    attachQueueRef.current = Promise.resolve();
    setBugAttaching(0);
  }, [attachSessions]);
  const [bugCapturing, setBugCapturing] = useState(false); // screenshot capture in progress (drives disabled/label UI)
  const bugCapturingRef = useRef(false); // synchronous lock — state batching can't gate a same-tick double-click, so this is authoritative
  const [bugHidden, setBugHidden] = useState(false); // modal hidden (not unmounted) during capture so it's not in the shot

  // `via`: how the modal was reached — "button" (any feedback button; a click handler may
  // pass its event here), "shortcut", "gesture" or "chat".
  const openBugReport = useCallback(
    (via) => {
      newAttachSession();
      setBugOpen(true);
      setBugMode("bug");
      setBugScreenshots([]);
      bugRetryRef.current = null;
      bugFormGenRef.current += 1;
      recordUiEvent({ event: "ui.bug_report.open", via: typeof via === "string" ? via : "button" });
    },
    [setBugScreenshots, newAttachSession],
  );
  // Append images to the report, up to the cap; `source` says how they arrived.
  const addScreenshots = useCallback(
    (shots, source) => {
      const room = MAX_BUG_SCREENSHOTS - bugScreenshotsRef.current.length;
      const added = shots.filter(Boolean).slice(0, Math.max(0, room));
      if (added.length > 0) setBugScreenshots([...bugScreenshotsRef.current, ...added]);
      recordUiEvent({
        event: "ui.bug_report.screenshot_capture",
        captured: added.length > 0,
        report_type: bugMode === "feature" ? "feature_request" : "bug",
        source,
        count: bugScreenshotsRef.current.length,
      });
    },
    [bugMode, setBugScreenshots],
  );
  const removeScreenshot = useCallback(
    (index) => setBugScreenshots(bugScreenshotsRef.current.filter((_, i) => i !== index)),
    [setBugScreenshots],
  );
  // Attach image files pasted, dropped or picked from disk (`source`: paste | drop | file).
  // Each is re-encoded (see lib/bugReportImages.js). Batches decode in parallel but are added
  // in the order they began, so the report keeps the user's order and, at the cap, an earlier
  // batch is never displaced by a later one that decoded first.
  // Take the next turn in the current session's queue: once every batch begun before it is
  // added, wait for `shots` (a promise of data URLs) and add them, unless the session ended.
  const queueAttach = useCallback(
    (token, shots, source, { reportFailures = true } = {}) => {
      const turn = attachQueueRef.current.then(async () => {
        const settled = await shots;
        if (!endAttach(token)) return;
        if (reportFailures && settled.some((s) => !s)) toast(ATTACH_FAILED, { kind: "error" });
        addScreenshots(settled, source);
      });
      // A rejected turn is a lost attach: logged and counted, and the queue still settles.
      attachQueueRef.current = turn.catch((err) => reportFailure("bug_report.attach_failed", err));
      return turn;
    },
    [addScreenshots, endAttach],
  );
  const attachImageFiles = useCallback(
    (files, source) => {
      // A full form takes nothing more; below the cap, addScreenshots trims each batch to the room
      // left when its turn comes.
      if (bugScreenshotsRef.current.length >= MAX_BUG_SCREENSHOTS) return Promise.resolve();
      const picked = Array.from(files || []).slice(0, MAX_BUG_SCREENSHOTS);
      if (picked.length === 0) return Promise.resolve();
      const token = beginAttach();
      const decoding = Promise.all(
        picked.map((f) =>
          imageFileToDataUrl(f).catch((e) => {
            console.warn("[Sayer] bug-report image not attached:", e);
            return "";
          }),
        ),
      );
      return queueAttach(token, decoding, source);
    },
    [beginAttach, queueAttach],
  );
  // Pasting an image anywhere while the modal is open attaches it; text pastes as usual.
  useEffect(() => {
    if (!bugOpen) return undefined;
    const onPaste = (e) => {
      const files = imageFilesFrom(e.clipboardData);
      if (files.length === 0) return;
      e.preventDefault();
      attachImageFiles(files, "paste");
    };
    // telemetry-gesture: attaches a pasted image to the form bug_report.submit sends; ui.bug_report.screenshot_capture records it
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [bugOpen, attachImageFiles]);
  // Capture a screenshot on demand (from the "Capture screen" button) and append it to the
  // report's images. Works for both bugs and feature requests.
  const captureScreenshot = useCallback(async () => {
    // Synchronous lock: a same-tick double-click would slip past a state-only
    // guard (setBugCapturing is batched), firing two getDisplayMedia prompts.
    if (bugCapturingRef.current) return;
    if (bugScreenshotsRef.current.length >= MAX_BUG_SCREENSHOTS) return;
    bugCapturingRef.current = true;
    setBugCapturing(true);
    // The capture takes its queue turn now, at the click, so it lands after batches already
    // decoding and, at the cap, never displaces them. A denied capture is not an error.
    const token = beginAttach();
    let captured;
    queueAttach(token, new Promise((r) => (captured = r)), "capture", { reportFailures: false });
    // Hide the modal (visibility, not unmount — preserves typed text) so it
    // isn't in the screenshot. Two RAFs so the browser paints the hidden state
    // before getDisplayMedia samples the screen.
    setBugHidden(true);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    let shot = "";
    let stream = null;
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: { displaySurface: "browser" },
          preferCurrentTab: true,
        });
        const video = document.createElement("video");
        video.srcObject = stream;
        await video.play();
        shot = encodeCanvas(drawToCanvas(video, window.innerWidth, window.innerHeight));
      }
    } catch (_e) {
      /* permission denied or unavailable — leave screenshot empty */
    } finally {
      // Always stop the capture tracks — even if video.play()/drawImage rejects
      // — so screen capture can't keep running (privacy/resource leak).
      if (stream) stream.getTracks().forEach((t) => t.stop());
    }
    bugCapturingRef.current = false;
    setBugHidden(false);
    setBugCapturing(false);
    captured([shot]);
  }, [beginAttach, queueAttach]);
  const submitBug = useCallback(async () => {
    // Images still decoding would be left out of the report: Send waits for them.
    if (!bugDesc.trim() || bugSubmitting || attachSessions.pending > 0) return;
    const isFeature = bugMode === "feature";
    const desc = bugDesc.trim();
    const steps = bugSteps.trim();
    // Snapshot the trace inputs at click time, BEFORE the modal-close re-render
    // and the RAF / screen-capture awaits below — during those awaits a
    // background completion or a forecast switch could change active/activeTrace
    // and stamp the report with a different forecast than the one the user was
    // looking at. Anchor on the active forecast (its traceId is stamped at
    // traceAllocated and survives on the object); activeTrace/lastObs are
    // fallbacks. See resolveBugReportTrace + bug cf6e0768.
    const { traceId: bugTraceId, backend: bugBackend } = resolveBugReportTrace({
      active,
      activeTrace,
      lastObs,
    });
    // The confirmation line is filed in the same forecast's chat, for the same reason.
    const replyTarget = captureChatTarget(getRunState());
    // Screenshots are attached ahead of time (captured, pasted, dropped or picked);
    // [] when the user attached none.
    const screenshots = bugScreenshots;
    newAttachSession();
    setBugSubmitting(true);
    setBugOpen(false);
    // Record submit BEFORE snapshot() so the "user clicked submit" event
    // is part of the bundle. Without this the bundle's last entry would be
    // an unrelated upstream event, leaving "what did the user do right
    // before reporting?" implicit.
    // The user's report (P2 design §4.6), opened before the rings are snapshotted so the
    // bundle carries it; the request carries it as x-intent-id.
    const intent = beginIntent("bug_report.submit", {}, { origin: "bug-report.submit" });
    recordUiEvent({
      event: "ui.bug_report.submit",
      report_type: isFeature ? "feature_request" : "bug",
      has_screenshot: screenshots.length > 0,
      screenshot_count: screenshots.length,
      trace_id: bugTraceId,
    });
    // Snapshot the rendered layout (container scroll + drawer geometry + viewport)
    // right before the buffer is captured, so layout bugs invisible to the event
    // stream (e.g. the drawer-shift bug 485fb71d) are diagnosable from the bundle.
    recordUiEvent(captureLayoutSnapshot());
    const logEvents = ringBuffer.snapshot();
    const traceEvents = traceRing.snapshot();
    const sessionFields = bugReportSessionFields({
      logEvents: [...logEvents, ...traceEvents],
      tabId: telemetryTabId(),
      buildSha: CLIENT_BUILD_SHA,
      location: window.location,
      visitId: p2Telemetry().visitId,
      tabStart: p2Telemetry().tabStartSnapshot(),
    });
    // True only once the server confirmed a sink accepted the report (`ok: true`).
    let stored = false;
    const formGen = bugFormGenRef.current;
    try {
      // Reporter identity (email/name) comes from the Zitadel session on the
      // server side. forecaster_trace_id links the bug to the user's most
      // recent forecast for fast diagnostic lookup via sooth-sql /trace.
      const r = await postBugReport(
        {
          report_type: isFeature ? "feature_request" : "bug",
          description: desc,
          steps_to_reproduce: isFeature ? null : steps || null,
          forecaster_trace_id: bugTraceId,
          forecast_backend: bugBackend,
          ...sessionFields,
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          screenshots,
          client_log_events: logEvents,
          client_trace_events: traceEvents,
          ...(bugRetryRef.current
            ? { retry_of: bugRetryRef.current.bugId, retry_token: bugRetryRef.current.token }
            : {}),
        },
        { intentId: intent.id },
      );
      const data = await r.json();
      intent.end(data.ok ? "ok" : "error");
      stored = data.ok === true;
      // A not-stored answer with the pair arms the next submit's reuse; a stored report or an
      // answer without it (an older server, a fresh id) disarms it. A fresh form opened
      // meanwhile owns the ref (it cleared the pair) and this answer leaves it alone.
      if (bugFormGenRef.current === formGen)
        bugRetryRef.current =
          !stored && typeof data.bug_id === "string" && typeof data.retry_token === "string"
            ? { bugId: data.bug_id, token: data.retry_token }
            : null;
      if (stored) {
        if (data.bug_id)
          setLastBugObs({
            bugId: data.bug_id,
            requestId: r.headers.get("x-request-id") || null,
            ts: new Date().toISOString(),
          });
        // Persisted like the rest of the chat, so it also durably marks which
        // forecast the user reported an issue on. Via the ref because pushSayerMsg
        // is defined after this hook in AppInner.
        pushSayerMsgRef?.current?.("Thanks, your feedback was sent.", { target: replyTarget });
        toast("Thanks, your feedback was sent.", { kind: "success" });
      } else {
        console.error("[Sayer] feedback submit refused:", data);
        pushSayerMsgRef?.current?.(FEEDBACK_FAILED, { target: replyTarget });
        toast(FEEDBACK_FAILED, { kind: "error" });
      }
    } catch (e) {
      intent.end("error");
      console.error("[Sayer] feedback submit failed:", e);
      pushSayerMsgRef?.current?.(FEEDBACK_FAILED, { target: replyTarget });
      toast(FEEDBACK_FAILED, { kind: "error" });
    }
    if (stored) {
      setBugDesc("");
      setBugSteps("");
      setBugScreenshots([]);
      setBugMode("bug");
    } else {
      // Nothing stored the report (the server answered ok:false — no sink accepted the row —
      // or the request never reached it): the draft stays, and the form reopens with it so
      // the reporter can retry; openBugReport would start a fresh form instead.
      setBugOpen(true);
    }
    setBugSubmitting(false);
  }, [bugDesc, bugSteps, bugSubmitting, bugMode, bugScreenshots, lastObs, active, activeTrace]); // eslint-disable-line react-hooks/exhaustive-deps -- setLastBugObs is a stable useState setter; eslint can't infer stability through the hook boundary

  return {
    bugOpen,
    setBugOpen,
    bugDesc,
    setBugDesc,
    bugSteps,
    setBugSteps,
    bugSubmitting,
    bugMode,
    setBugMode,
    bugScreenshots,
    bugAttaching: bugAttaching > 0,
    bugCapturing,
    bugHidden,
    openBugReport,
    captureScreenshot,
    attachImageFiles,
    removeScreenshot,
    submitBug,
  };
}
